import { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as apiUser from 'api/apiUsers';
import { useAuth } from 'context/AuthContext';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import TitleCounter from 'components/others/TitleCounter';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import { useRegistryColumns } from 'utils/helpers/list/useRegistryColumns';
import OffUserCard from './OffUserCard';
import UsersTable from './UsersTable';
import UsersToolBar from './UsersToolBar';
import { defaultUserListFilter, readUserListFilter, type UserListFilterState } from './model/userListFilter';
import type { UserListColumnId } from './types';
import type { UserType } from '@econtractor/types';

const USER_COLUMN_DEFS: { id: UserListColumnId; defaultVisible: boolean }[] = [
    { id: 'role', defaultVisible: false },
    { id: 'team', defaultVisible: false },
    { id: 'admin', defaultVisible: false },
];

const Users = () => {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const { g } = useGlobal();
    const { showToast } = useToast();
    const filterName = `voltox-${auth.user.ID}-users-filter`;
    const [listFilter, setListFilter] = useState<UserListFilterState>(() =>
        readUserListFilter(safeJSONParse(sessionStorage.getItem(filterName))),
    );
    const [usersList, setUsersList] = useState<UserType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const location = useLocation();
    const onPage = useListOnPage();
    const prevUserData = useRef<string | null>(null);
    const showSpinnerRef = useRef(showSpinner);
    const [page, setPage] = useState<number | null>(null);
    const [showUserCard, setShowUserCard] = useState<number | false>(false);
    const { visible: listColumns, toggle: toggleListColumn } = useRegistryColumns(
        'registryColumns:user',
        USER_COLUMN_DEFS,
    );
    const showRole = Boolean(listColumns.role);
    const showTeam = Boolean(listColumns.team);
    const showAdmin = Boolean(listColumns.admin);

    showSpinnerRef.current = showSpinner;

    const fetchUsers = async (force = false) => {
        if (force) prevUserData.current = null;
        if (!page) return null;
        // При force не блокуємо через showSpinner (інакше після збереження картки список не оновлюється)
        if (!force && showSpinnerRef.current) return null;

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const userData = {
            search,
            page: page < 0 ? 1 : page || 1,
            onPage,
            roles: listFilter.roles.length ? listFilter.roles : ['none'],
            status: listFilter.status.length ? listFilter.status : [-9999],
            ...(listFilter.teamId ? { teamId: listFilter.teamId } : {}),
        };

        if (prevUserData.current == JSON.stringify(userData)) return;
        prevUserData.current = JSON.stringify(userData);

        setShowSpinner(true);
        const resUser = await apiUser.getUsers(userData);
        setShowSpinner(false);
        if (!resUser) return showToast(t('common:messError'), 'danger');
        setUsersList(resUser?.users);

        g.setPaginator((prev) => ({
            ...prev,
            [location.pathname]: resUser.paginator,
        }));
    };

    const resetUsersList = () => {
        prevUserData.current = null;
        setListFilter(defaultUserListFilter());
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
    };

    useEffect(() => {
        sessionStorage.setItem(filterName, JSON.stringify(listFilter));
    }, [listFilter, filterName]);

    useEffect(() => {
        if (page == 1) {
            fetchUsers(true);
        } else setPage(1);
    }, [g.search?.[location.pathname], g.target, listFilter]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page != _page) setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, g?.paginator?.[location.pathname]?.countRows, g?.paginator?.[location.pathname]?.onPage]);

    useEffect(() => {
        fetchUsers();
    }, [page, onPage]);

    return (
        <>
            <OffUserCard
                showUserCard={showUserCard}
                setShowUserCard={setShowUserCard}
                fetchUsers={fetchUsers}
                setShowSpinner={setShowSpinner}
            />
            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('Users.title')}</h4>
                            <TitleCounter />
                        </div>
                        <div>
                            <UsersToolBar
                                setShowUserCard={setShowUserCard}
                                showRole={showRole}
                                showTeam={showTeam}
                                showAdmin={showAdmin}
                                onToggleColumn={(id) => toggleListColumn(id)}
                                onRefresh={() => {
                                    void fetchUsers(true);
                                }}
                                onReset={resetUsersList}
                                filter={listFilter}
                                onApplyFilter={setListFilter}
                            />
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                    <div className="px-0">
                            {usersList ? (
                                <UsersTable
                                    usersList={usersList}
                                    fetchUsers={fetchUsers}
                                    setShowUserCard={setShowUserCard}
                                    showSpinner={showSpinner}
                                    setShowSpinner={setShowSpinner}
                                    showRole={showRole}
                                    showTeam={showTeam}
                                    showAdmin={showAdmin}
                                />
                            ) : (
                                <ESpinner />
                            )}
                        </div>
                </Col>
            </Row>
        </>
    );
};

export default Users;
